import { Browser, DeviceInfo } from "Device/Info/DeviceInfo";
import { DataConverter } from "../Converters/DataConverter";
import { GlobalContext } from "../GlobalContext";
import { StringUtils } from "../Utils/StringUtils";
import { IStreamDecryptor } from "./IStreamDecryptor";
import { IStreamEncryptor } from "./IStreamEncryptor";

export type CryptoKeyUsage = "encrypt" | "decrypt" | "verify" | "sign" | "wrapKey" | "unwrapKey" | "deriveKey" | "deriveBits";

/**Nastavení pro webcrypto import*/
export interface IWebCryptoAlgSettings extends Algorithm {
    hash?: IWebCryptoHashSettings;
    namedCurve?: string;
    length?: number;
}

/**Nastavení hash algoritmu pro webcrypto*/
export interface IWebCryptoHashSettings {
    name: string;
}

const _registeredCryptors: Map<string, new (key: CryptoKey, iv?: BufferSource, addIVtoFirstBlock?: boolean) => IStreamEncryptor | IStreamDecryptor> = new Map();
let _webCryptoSupport: boolean | undefined;

export class SecurityUtils {
    /**
     * Vypočítá SHA-1 se zadaných data
     * @param data Data ze kterých se má vypočítat hash. Pokud jsou data string, tak se nejdřív převedou na UTF8 pole.
     * @description Chrome umožňuje použít tuto funkci jen pokud aplikace běží v bezpečném prostředí:  Only secure origins are allowed (see: https://goo.gl/Y0ZkNV).
     */
    public static sha1(data: string | Int8Array | Int16Array | Int32Array | Uint8Array | Uint16Array | Uint32Array | Uint8ClampedArray | Float32Array | Float64Array | DataView | ArrayBuffer): PromiseLike<ArrayBuffer> {
        if (typeof data === "string")
            data = StringUtils.stringToUTF8Bytes(data);

        return self.crypto.subtle.digest({ name: "sha-1" }, data);
    }

    /**
     * Vypočítá SHA-1 se zadaných data a vrátí hash jako Base64 (je možné, že převod do Base64 nebude úplně kompatibilní s ostatnimi systémy)
     * @param data Data ze kterých se má vypočítat hash. Pokud jsou data string, tak se nejdřív převedou na UTF8 pole.
     * @description Chrome umožňuje použít tuto funkci jen pokud aplikace běží v bezpečném prostředí:  Only secure origins are allowed (see: https://goo.gl/Y0ZkNV).
     */
    public static async sha1String(data: string | Int8Array | Int16Array | Int32Array | Uint8Array | Uint16Array | Uint32Array | Uint8ClampedArray | Float32Array | Float64Array | DataView | ArrayBuffer): Promise<string> {
        const hash = await SecurityUtils.sha1(data);
        return DataConverter.bufferToBase64(hash);
    }

    public static async passwordToDeriveKey(password: string, salt: string, iterations: number = 4096, cipherMode: string = "CBC", exportable: boolean = true): Promise<CryptoKey> {
        return this.derivePasswordToAES256Key(await this.passwordToCryptoKey(password), salt, iterations, cipherMode, exportable);
    }

    /**
     * Převede heslo z řetězce na třídu CryptoKey
     * @param password heslo
     * @param algorithm algoritmus, pro který má být CryptoKey určen
     */
    public static passwordToCryptoKey(password: string, algorithm: string = "PBKDF2"): PromiseLike<CryptoKey> {
        const bytes = StringUtils.stringToUTF8Bytes(password);
        return self.crypto.subtle.importKey("raw", bytes, algorithm, false, ["deriveKey", "deriveBits"]);
    }

    /**
     * Derivuje heslo na 256 bit klíč pro AES (kompatibilní s .NET TescoSW.Global.Cryptography.PasswordDerivationFunction.GetKey256)
     * @param passwordKey zadané heslo - ideálně transformované metodou passwordToCryptoKey
     * @param salt sůl
     * @param iterations počet iterací algoritmu PBKDF2
     * @param cipherMode režim šifrování např: CBC, CTR, GCM, CFB, ...
     */
    public static derivePasswordToAES256Key(passwordKey: CryptoKey, salt: string, iterations: number = 4096, cipherMode: string = "CBC", exportable: boolean = true): PromiseLike<CryptoKey> {
        return self.crypto.subtle.deriveKey(
            {
                "name": "PBKDF2",
                salt: StringUtils.stringToUTF8Bytes(salt),
                iterations: iterations,
                hash: { name: "SHA-1" },
            },
            passwordKey,
            { name: "AES-" + cipherMode, length: 256 }, exportable, ["encrypt", "decrypt"]);
    }

    /**
     * Exportuje klíč
     */
    public static async exportKey(key: CryptoKey): Promise<ArrayBuffer> {
        if (!key.extractable)
            throw new Error("Cannot export CryptoKey. Only extractable keys are allowed.");
        return await self.crypto.subtle.exportKey("raw", key);
    }

    /**
     * Importuje klíč
     */
    public static async importKey(jwk: JsonWebKey, settings: IWebCryptoAlgSettings, usage: CryptoKeyUsage[] = ["encrypt", "decrypt"]): Promise<CryptoKey> {
        return self.crypto.subtle.importKey("jwk", jwk, (settings as any), false, usage);
    }

    /**
     * Zakóduje data do UTF8 a zašifruje zadaným klíčem
     * @param data data pro zašifrování
     * @param key šifrovací klíč
     */
    public static async encryptString(data: string, key: CryptoKey): Promise<ArrayBuffer> {
        /*
        let bytes = StringUtils.stringToUTF8Bytes(data);
        let iv = new Uint32Array(4); //128 bit
        let encData = await crypto.subtle.encrypt({ name: key.algorithm.name!, iv: crypto.getRandomValues(iv) }, key, bytes);
        return TescoSW.MemoryUtils.join32bitArrays(iv, new Uint32Array(encData)).buffer;
     */
        // optimalizace pro AES-CBC
        const { buffer: bytes, byteLength: l } = StringUtils.stringToUTF8Bytes(data);
        const r = new ArrayBuffer(l - (l % 16) + 32);
        const e = await self.crypto.subtle.encrypt({ name: key.algorithm.name!, iv: self.crypto.getRandomValues(new Uint32Array(r, 0, 4)) }, key, bytes as ArrayBuffer);
        (new Uint32Array(r)).set(new Uint32Array(e), 4);
        return r;
    }

    private static _cryptoPolyfillPromise: Promise<any>;

    /**
     * Polyfill pro dešifrování AES-CBC v Safari, které přestalo vyhazovat výjimku při dešifrování špatným klíčem.
     * @internal
     * @see https://bugs.webkit.org/show_bug.cgi?id=211777
     */
    public static loadAsmCryptoPolyfill(): Promise<any> {
        if (!this._cryptoPolyfillPromise)
            this._cryptoPolyfillPromise = importScript("js/asmcrypto/asmcrypto.all.es8.min.js");
        return this._cryptoPolyfillPromise;
    }
    /**
     * Dešifruje řetězec
     * @param data data pro dešifrování
     * @param key dešifrovací klíč
     */
    public static async decryptString(data: BufferSource, key: CryptoKey): Promise<string> {
        try {
            const { byteOffset: offset = 0, byteLength: length, buffer: buffer = data as ArrayBuffer } = data as ArrayBufferView;
            const iv = new Uint8Array(buffer, offset, 16); // nedochází ke kopírování paměti, jen se nastaví pohledny na části bufferu
            const bytes = new Uint8Array(buffer, offset + 16, length - 16);
            if (DeviceInfo.deviceProperties.browser === Browser.safari)
                return SecurityUtils._decryptStringPolyfill(key, iv, bytes);
            else
                return SecurityUtils._decryptStringNative(key, iv, bytes);
        } catch (exception) {
            throw new Error("Unable to decrypt string. Possibly wrong password");
        }
    }

    /** Nativní dešifrování pomocí WebCrypto Api */
    private static async _decryptStringNative(key: CryptoKey, iv: Uint8Array, data: Uint8Array): Promise<string> {
        const decData = await self.crypto.subtle.decrypt({ name: key.algorithm.name!, iv: iv }, key, data);
        return StringUtils.utf8BytesToString(decData);
    }

    /**
     * Polyfill pro dešifrování AES-CBC v Safari, které přestalo vyhazovat výjimku při dešifrování špatným klíčem.
     * Místo toho vrací náhodná data. Nejsme tak schopni detekovat neúspěšné dešifrování.
     * @see https://bugs.webkit.org/show_bug.cgi?id=211777
     */
    private static async _decryptStringPolyfill(key: CryptoKey, iv: Uint8Array, data: Uint8Array): Promise<string> {
        await this.loadAsmCryptoPolyfill();
        const keyData = await self.crypto.subtle.exportKey("raw", key);
        //@ts-ignore asmcrypto není modul, načte se do globálních proměnných
        const cbc = new AES_CBC(new Uint8Array(keyData), iv);
        return StringUtils.utf8BytesToString(cbc.decrypt(data));
    }

    /**
     * Pro zadaný klíč vrací třídu podporující streamové šifrování požadovaným algoritmem.
     * @param key Šifrovací klíč
     * @param addIVtoFirstBlock Jesli na začátek šifrovaných dat má být přidán IV
     * @param iv IV pokud ho chceme explicitně definovat
     */
    public static getStreamEncryptor(key: CryptoKey, iv?: BufferSource, addIVtoFirstBlock: boolean = true): IStreamEncryptor {
        const encryptor = _registeredCryptors.get(key.algorithm.name + "#ENC");
        if (encryptor)
            return new encryptor(key, iv, addIVtoFirstBlock) as IStreamEncryptor;

        throw new Error(`Unsupported algorithm: ${key.algorithm.name}!`); //`
    }

    /**
     * Pro zadaný klíč vrací třídu podporující streamové dešifrování požadovaným algoritmem.
     * @param key dešifrovací klíč
     * @param ivInFirstBlock Jesli na začátku šifrovaných dat je IV
     * @param iv IV pokud ho chceme explicitně definovat
     */
    public static getStreamDecryptor(key: CryptoKey, iv?: BufferSource): IStreamDecryptor {
        const encryptor = _registeredCryptors.get(key.algorithm.name + "#DEC");
        if (encryptor)
            return new encryptor(key, iv) as IStreamDecryptor;

        throw new Error(`Unsupported algorithm: ${key.algorithm.name}!`); //`
    }

    /**
     * Pro registraci tříd implementujících konkrétní proudové šifrování
     * @param cryptor Třída implementující IStreamEncryptor, nebo IStreamDecryptor
     * @param algorithmName Jméno šifrovacího algoritmu. Např. AES-CBC, AES-CFB, AES-CTR, ...
     */
    public static registerStreamCryptor(cryptor: new (key: CryptoKey, iv?: BufferSource, addIVtoFirstBlock?: boolean) => IStreamEncryptor | IStreamDecryptor, algorithmName: string): void {
        if (typeof cryptor === "function" && typeof cryptor.prototype === "object") {
            if (Reflect.has(cryptor.prototype, "encryptBlock"))
                _registeredCryptors.set(algorithmName + "#ENC", cryptor);
            if (Reflect.has(cryptor.prototype, "decryptBlock"))
                _registeredCryptors.set(algorithmName + "#DEC", cryptor);
        }
    }

    /**
     * Detekuje jestli prohlížeč podporuje WebCrypto API v takové míře v jaké potřebuje aplikace
     */
    public static async detectWebCryptoSupport(): Promise<boolean> {
        if (typeof _webCryptoSupport === "undefined") {
            try {
                if ((typeof self.crypto === "object") && (typeof self.crypto.subtle === "object")) {
                    const pass = await SecurityUtils.passwordToCryptoKey("žlutý kůň");
                    const key = await SecurityUtils.derivePasswordToAES256Key(pass, "{1234-5678-9123-4567-8912}");
                    const text = "žlutý kůň, žlutý kůň";
                    const enc = await SecurityUtils.encryptString(text, key);
                    return _webCryptoSupport = (await SecurityUtils.decryptString(enc, key) === text);
                }
            } catch (e) {
                // Prázdný catch
            }
            _webCryptoSupport = false;
        }
        return _webCryptoSupport;
    }

    /**
     * Převede pole bytů na binární string
     * @param data Pole bytů k převedení
     */
    public static arrayToBinaryEncodedString(data: Uint8Array): string {
        // bug max callstack size in workers http://code.google.com/p/chromium/issues/detail?id=252492
        const maxSize = 16 * 1024;
        let result = "";
        let tmp = data;
        while (tmp.length) {
            const partLength = Math.min(tmp.length, maxSize);
            result += String.fromCharCode.apply(null, data.subarray(0, partLength) as any as number[]); // String.fromCharCode bere i Uint8Array, pouze to není v typingu
            tmp = tmp.subarray(partLength);
        }
        return result;
    }

    /**
     * Vytvoří kryptograficky silný klíč pomocí pseudonáhodného generátoru čísel
     * @param byteCount Počet bytů entropie. Za každý byte vzniknou dva znaky ve výsledném řetězcovém klíči.
     */
    public static generateStrongKey(byteCount: number = 16): string {
        const array = new Uint8Array(byteCount);
        crypto.getRandomValues(array);
        return array.reduce((accumulator, current) => accumulator += current.toString(16).padStart(2, "0"), "");
    }
}

GlobalContext.registerType(SecurityUtils);
